import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import Amplify from "@aws-amplify/core";
import { MdChatBubble } from "react-icons/md";

import * as realTimeApi from "../../real-time-api-config";
import { defaultChatBotSettings } from "../../consts/settings";
import { useAppInfo, useQuery } from "../../helpers/hooks/common-hook";

import VoiceModePlugin from "../../pages/voice-mode";
import PlayAudio from "../../components/play-audio-v2";
import { ReplyWaitIndicator } from "../question-ask/conv-message/reply-wait-indicator";
import {
  createBotMessagePayload,
  createSelfMessagePayload,
} from "../../helpers/utils/message-utils";
import { getConfig } from "../../services/config";

import ChatConversation from "./chat-conversation";
import ChatHeader from "./chat-header";
import ChatFooter from "./chat-footer";
import ChatForm from "./chat-form";
import {AUTH_KEY} from "../../consts/common-consts";
import {useCookies} from "react-cookie";

const { customAlphabet } = require("nanoid");
const customNanoid = customAlphabet("1234567890abcdefghikkl", 18);

Amplify.configure(realTimeApi.config);

function getNewSessionId() {
  return customNanoid(15);
}

function getRequestAttribute(requestAttributesParam) {
  try {
    return JSON.parse(atob(requestAttributesParam));
  } catch (e) {
    return {}
  }
}

const dummyMessages = [];

function ChatPluginTask({ onChangeInspectData, voiceModeChangeNotifyMessage }) {
  const [ cookies ] = useCookies(["cookie-name"]);
  const query = useQuery();
  const { appId, accessKey } = useAppInfo();

  const plugin = query.get("plugin");
  const voiceParam = query.get("voice");
  const sessionIdParam = query.get("session_id");
  const requestAttributesParams = getRequestAttribute(query.get("request_attributes"));

  const [sessionId, setSessionId] = useState();
  const [botReply, setBotReply] = useState();
  const [botReplyWait, setBotReplyWait] = useState(false);
  const [messages, setMessages] = useState(dummyMessages || []);
  const [voiceAudioSource, setVoiceAudioSource] = useState();
  const [initReconnect, setInitReconnect] = useState({});

  const [formState, setFormState] = useState({
    processing: false,
    success: false,
    failed: false,
    errorMessage: "",
  });

  // if a plugin, by default, initiated with close state
  const [close, setClose] = useState(plugin);

  const [voiceMode, setVoiceMode] = useState(!!voiceParam);

  const [chatBotSetting, setChatBotSetting] = useState(defaultChatBotSettings);

  const [settingsState, setSettingsState] = useState({
    processing: false,
    success: false,
    failed: false,
    errorMessage: "",
  });

  useEffect(() => {
    setSessionId(sessionIdParam ||  getNewSessionId())
  }, [sessionIdParam]);
  
  // Listen for messages from parent window
  useEffect(() => {
    const handleParentMessage = (event) => {
      // Check if the message is from the parent window
      if (event.source === window.parent) {
        // Handle toggle command from parent
        if (event.data && event.data.type === 'botcircuits_parent_toggle') {
          onClickToggleClose(!event.data.isOpen);
        }
      }
    };
    
    // Add event listener
    window.addEventListener('message', handleParentMessage);
    
    // Clean up
    return () => {
      window.removeEventListener('message', handleParentMessage);
    };
  }, []);

  useEffect(() => {
    fetchChatBotSettings();
  }, []);

  useEffect(() => {
    if (sessionId && messages.length === 0) {
      const question = "Hi";
      const requestAttributes = {};
      sendMessageToBot(question, requestAttributes, false);
    }
  }, [sessionId, messages.length]);

  useEffect(() => {
    if (chatBotSetting.title) {
      document.title = chatBotSetting.title;

      let link = document.querySelector("link[rel~='icon']");
      if (!link) {
        link = document.createElement("link");
        link.rel = "icon";
        document.getElementsByTagName("head")[0].appendChild(link);
      }
      link.href = chatBotSetting.logo;
    }
  }, [chatBotSetting]);

  useEffect(() => {
    if (voiceModeChangeNotifyMessage) {
      console.log("voiceModeChangeNotifyMessage", voiceModeChangeNotifyMessage);
      setVoiceMode(voiceModeChangeNotifyMessage.status);
    }
  }, [voiceModeChangeNotifyMessage]);

  useEffect(() => {
    console.log("sessionId change", sessionId);
  }, [sessionId]);

  const updateSettingsState = (processing, success, failed, errorMessage) => {
    setSettingsState(
      Object.assign({}, { processing, success, failed, errorMessage })
    );
  };

  const fetchChatBotSettings = async () => {
    updateSettingsState(true, false, false, "");
    try {
      const resp = await getConfig(`app_${appId}`, "web_integration", accessKey);
      if (resp && resp.data) {
        setChatBotSetting(resp.data);
        updateSettingsState(false, true, false, "");
      } else {
        updateSettingsState(false, false, true, "Failed to load settings");
      }
    } catch (error) {
      updateSettingsState(false, false, true, "Failed to load settings");
    }
  };

  const onClickToggleClose = (state) => {
    // if not a plugin. close effect will not affect
    if (!plugin) return;

    setClose(state);
    
    // Send message to parent window about state change
    if (window.parent && window.parent !== window) {
      window.parent.postMessage({
        type: 'botcircuits_toggle_state',
        isOpen: !state
      }, '*');
    }
  };

  const initSubscription = () => {
    const subscription = realTimeApi.subscribeBotMessage(
      accessKey,
      appId,
      sessionId,
      ({ data }) => {
        data = JSON.parse(data);
        setBotReply(data);
        setBotReplyWait(false);
        onChangeInspectData(data);

        if (data.message.voiceMessage) {
          setVoiceAudioSource(data.message.voiceMessage);
        }
      },
      (error) => {
        if (error.error.errors[0].message === "Connection closed") {
          console.error("CONN FAILED", error);
          setInitReconnect({});
        }
      }
    );
    console.log("subscription", subscription);
    return () => {
      console.log("unsubscribe......");
      subscription.unsubscribe();
    };
  };

  const updateFormState = (processing, success, failed, errorMessage) => {
    setFormState(
      Object.assign({}, { processing, success, failed, errorMessage })
    );
  };

  function getAuthCookieValue() {
    try {
      const cookieString = document.cookie;
      const cookies = cookieString.split('; ');

      for (let cookie of cookies) {
        const [cookieName, cookieValue] = cookie.split('=');
        if (cookieName === AUTH_KEY) {
          return JSON.parse(decodeURIComponent(cookieValue));
        }
      }
    } catch (e) {}
    return null;
  }

  const sendMessageToBot = async (
    reqQuestion,
    requestAttributes = {},
    appendToUI = true
  ) => {
    if (!formState.processing) {
      if (appendToUI) {
        appendMessage(createSelfMessagePayload(requestAttributes.title || reqQuestion));
      }
      updateFormState(true, false, false);
      setBotReplyWait(true);

      const savedAuthData = getAuthCookieValue()
      if (savedAuthData) {
        requestAttributes['auth'] = savedAuthData
      }

      await realTimeApi.sendUserMessage(
        accessKey,
        appId,
        sessionId,
        JSON.stringify({
          action: "executor",
          appId,
          sessionId,
          inputText: reqQuestion,
          requestAttributes: { ...requestAttributes, ...requestAttributesParams },
        })
      );
      updateFormState(false, true, false);
    }
  };

  const handleOnAskSubmit = async (question, requestAttributes = {}) => {
    await sendMessageToBot(question, requestAttributes);
  };

  const onInvokeAction = async (payload, requestAttributes = {}) => {
    await sendMessageToBot(payload, requestAttributes);
  };

  const onChangeVoiceInvokeState = async (state) => {
    const { processing, success, failed } = state;
    setBotReplyWait(processing);
  };

  const appendMessage = (message) => {
    const newMessages = [...messages, message];
    setMessages(newMessages);
  };

  useEffect(() => {
    if (sessionId) {
      return initSubscription();
    }
  }, [sessionId]);

  useEffect(() => {
    console.log("initReconnect");
    if (sessionId) {
      return initSubscription();
    }
  }, [initReconnect]);

  useEffect(() => {
    if (botReply) {
      appendMessage(createBotMessagePayload(botReply.message));
    }
  }, [botReply]);

  const toggleVoiceMode = () => {
    setVoiceMode((prevMode) => !prevMode);
    setSessionId(sessionIdParam || getNewSessionId()); // Reset session ID
    setMessages([]); // Clear messages
    setBotReply(null); // Clear bot reply
  };

  if (close) {
    return (
      <div
        onClick={() => onClickToggleClose(false)}
        style={{
          backgroundColor: chatBotSetting.headerBackgroundColor,
        }}
        className="group absolute bottom-10 right-10 w-[4rem] h-[4rem] rounded-full flex items-center justify-center
        bg-indigo-600 shadow cursor-pointer transition-all duration-300 ring-indigo-100 ring-offset-2 hover:ring-4 active:scale-90"
      >
        {chatBotSetting.logo ?
            <>
              <div className="w-12 h-12 flex items-center justify-center p-1">
                <img
                    src={chatBotSetting.logo}
                    alt="logo"
                    className="w-full h-full object-cover"
                />
              </div>
            </>
            : <MdChatBubble
                size={21}
                className="fill-white group-hover:animate-pulse"
            />
        }

      </div>
    );
  }

  if (voiceMode) {
    return (
      <VoiceModePlugin
        appId={appId}
        accessKey={accessKey}
        sessionId={sessionId}
        voiceAudioSource={voiceAudioSource}
        botReplyIn={botReply}
        onInvokeAction={onInvokeAction}
        settingsState={settingsState}
        chatBotSetting={chatBotSetting}
        toggleVoiceMode={toggleVoiceMode}
      />
    );
  }

  return (
    <div style={{ height: "100dvh" }} className="h-screen bg-transparent">
      <div className="plugin-chat-wrapper flex h-full flex-1 flex-col justify-between">
        {/* HEADER */}
        <ChatHeader
          settingsState={settingsState}
          chatBotSetting={chatBotSetting}
          toggleVoiceMode={toggleVoiceMode}
          appId={appId}
          accessKey={accessKey}
          sessionId={sessionId}
          voiceAudioSource={voiceAudioSource}
          botReplyIn={botReply}
          onInvokeAction={onInvokeAction}
          onClose={()=>onClickToggleClose(true)}
        />
        {!voiceAudioSource && (
          <>
            <PlayAudio audioSourceData={voiceAudioSource} botReply={botReply} />
          </>
        )}
        {/* CHAT MESSAGER */}
        <div className="w-full max-w-screen-md mx-auto flex flex-1 flex-grow overflow-hidden bg-white">
          <div className="plugin-chat-view flex flex-1 flex-col justify-between">
            <div className="chat-message-wrapper hide-scrollbars mt-2 py-4 px-4 lg:px-2 flex flex-col flex-1 flex-grow overflow-y-auto">
              {messages.map((message, idx) => {
                return (
                  <ChatConversation
                    key={idx}
                    sessionId={sessionId}
                    chatBotSetting={chatBotSetting}
                    onInvokeAction={onInvokeAction}
                    {...message}
                  />
                );
              })}
              {botReplyWait && (
                <ReplyWaitIndicator chatBotSetting={chatBotSetting} />
              )}
            </div>

            {/* INPUT FORM */}
            <ChatForm
              onChangeInspectData={onChangeInspectData}
              onHandleSubmit={handleOnAskSubmit}
              chatBotSetting={chatBotSetting}
              sessionId={sessionId}
            />
          </div>
        </div>
        {/* FOOTER */}
        <ChatFooter chatBotSetting={chatBotSetting} />
      </div>
    </div>
  );
}

ChatPluginTask.propTypes = {
  onChangeInspectData: PropTypes.func,
  voiceModeChangeNotifyMessage: PropTypes.shape({
    status: PropTypes.bool,
  }),
};

export default ChatPluginTask;
